/**
 * Ward-level ELA proficiency data — verified 2025 DC CAPE values.
 *
 * Source: EmpowerK12 DC CAPE Dashboard (https://www.empowerk12.org/dc-cape-dashboard)
 * Filter set: ELA / 2025 / CAPE 3+ (Approach, Meet, or Exceed) / All Students / By Ward of School
 * Pulled: Feb 2026
 *
 * Notes:
 * - Ward percentages are EmpowerK12's weighted average of school-level
 *   unsuppressed data (the "estimate" methodology the dashboard discloses).
 * - Per EmpowerK12, all 8 wards increased ELA proficiency in 2025 vs. 2024
 *   — the largest single-year gain since computer-based testing began in 2015.
 * - Citywide ELA (All Students, 3+) moved 55% (23-24) → 58% (24-25), +3 pts.
 *   Ward-level 2024 → 2025 deltas were not exported per ward on the dashboard;
 *   this file therefore records the verified 2025 value plus the citywide
 *   directional indicator (improved = true) instead of a fabricated per-ward delta.
 *
 * Update annually each August when OSSE/EmpowerK12 publish new results.
 */
export const WARD_ELA_2025 = {
  "1": { proficiency_pct: 51, improved: true },
  "2": { proficiency_pct: 75, improved: true },
  "3": { proficiency_pct: 86, improved: true },
  "4": { proficiency_pct: 62, improved: true },
  "5": { proficiency_pct: 56, improved: true },
  "6": { proficiency_pct: 67, improved: true },
  "7": { proficiency_pct: 44, improved: true },
  "8": { proficiency_pct: 43, improved: true },
};

/** Citywide reference for tooltip context. */
export const WARD_ELA_CITYWIDE_2025 = {
  proficiency_pct: 58,
  prior_year_pct: 55,
  delta_pts: 3,
};

export const WARD_ELA_SOURCE = "EmpowerK12 / OSSE DC CAPE Dashboard — ELA, 2025, All Students, CAPE 3+, by Ward of School";

export const WARD_ELA_NOTE =
  "All 8 wards increased ELA proficiency from 2024 to 2025 — the largest single-year improvement since 2015. " +
  "Update annually from the EmpowerK12 DC CAPE Dashboard.";

/**
 * Bucketed red gradient — light pink → deep red, where darker = lower proficiency.
 * Buckets recalibrated to fairly distribute DC's actual 2025 distribution
 * (range 43–86%) across the visual scale.
 */
export function colorForProficiency(pct) {
  if (pct == null || Number.isNaN(pct)) return "#F5F5F4";
  if (pct >= 75) return "#FFEBEE";
  if (pct >= 60) return "#FFCDD2";
  if (pct >= 50) return "#EF9A9A";
  if (pct >= 45) return "#E57373";
  if (pct >= 40) return "#C62828";
  return "#B71C1C";
}

/**
 * For screen readers / a11y description.
 */
export const WARD_ELA_SR_DESCRIPTION =
  "Map showing DC ward boundaries shaded by 2025 ELA reading proficiency. " +
  "Darker red indicates lower proficiency. Ward 3 has the highest proficiency at 86%; " +
  "Wards 7 and 8 have the lowest at 44% and 43%. All 8 wards improved from 2024.";
